import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from 'styled-components';
import {
    Box, Typography, Paper, Container, Button,
    Grid, IconButton, Tooltip, CircularProgress,
    Fade, Chip, Avatar, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
    Campaign as CampaignIcon,
    AddCircle as AddCircleIcon,
    DeleteOutline as DeleteOutlineIcon,
    Edit as EditIcon,
    Event as EventIcon,
    Notes as NotesIcon,
    ErrorOutline as ErrorOutlineIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { getAllNotices } from '../../../redux/noticeRelated/noticeHandle';
import { deleteUser } from '../../../redux/userRelated/userHandle';
import { useToast } from '../../../context/ToastContext';
import { formatDatePart } from '../../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 40px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 400px;
        height: 400px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -80px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(20px) !important;
    padding: 32px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.05) !important;
    margin-bottom: 24px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 30px 60px -15px rgba(0, 0, 0, 0.1) !important;
    }
`;

const ActionBtn = styled(Button)`
    border-radius: 14px !important;
    padding: 10px 24px !important;
    font-weight: 800 !important;
    text-transform: none !important;
    box-shadow: 0 8px 16px rgba(99, 102, 241, 0.2) !important;
`;

const ShowNotices = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { noticesList, loading, error, response } = useSelector((state) => state.notice);
    const { currentUser } = useSelector(state => state.user);
    const { showToast } = useToast();

    const [deleteDialog, setDeleteDialog] = useState(false);
    const [noticeToDelete, setNoticeToDelete] = useState(null);

    useEffect(() => {
        dispatch(getAllNotices(currentUser._id, "Notice"));
    }, [currentUser._id, dispatch]);

    const handleDeleteClick = (id) => {
        setNoticeToDelete(id);
        setDeleteDialog(true);
    };

    const confirmDelete = () => {
        dispatch({ type: 'notice/getFailed', payload: null });
        dispatch(deleteUser(noticeToDelete, "Notice"))
            .then(() => {
                dispatch({ type: 'notice/getSuccess', payload: [] });
                dispatch(getAllNotices(currentUser._id, "Notice"));
                showToast("Notice deleted successfully", "success");
                setDeleteDialog(false);
            });
    };

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl" >
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <IconButton onClick={() => navigate('/Admin/dashboard')} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: '-0.03em', display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <CampaignIcon sx={{ fontSize: 48, color: '#6366f1' }} />
                                        School Notices
                                    </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 400, ml: 8 }}>
                                    Manage announcements and official communications.
                                </Typography>
                            </Box>
                            <ActionBtn 
                                variant="contained" 
                                color="primary" 
                                startIcon={<AddCircleIcon />}
                                onClick={() => navigate("/Admin/addnotice")}
                                sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                            >
                                Publish Notice
                            </ActionBtn>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{ mt: 5 }} >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress size={60} thickness={4} />
                    </Box>
                ) : response || (noticesList && noticesList.length === 0) ? (
                    <PremiumCard sx={{ p: 8, textAlign: 'center' }}>
                        <NotesIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>
                            No Notices Published
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                            You haven't published any official notices yet.
                        </Typography>
                        <ActionBtn 
                            variant="outlined" 
                            startIcon={<AddCircleIcon />}
                            onClick={() => navigate("/Admin/addnotice")}
                        >
                            Create First Notice
                        </ActionBtn>
                    </PremiumCard>
                ) : (
                    <Grid container spacing={3}>
                        {[...noticesList]
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((notice, index) => {
                            const dateString = notice.date ? formatDatePart(notice.date) : "No Date";
                            
                            return (
                                <Grid item xs={12} key={notice._id}>
                                    <PremiumCard sx={{ animationDelay: `${index * 0.1}s`, p: 4 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                                                <Avatar sx={{ 
                                                    width: 56, 
                                                    height: 56, 
                                                    bgcolor: '#eff6ff', 
                                                    color: '#3b82f6',
                                                    borderRadius: '16px'
                                                }}>
                                                    <CampaignIcon fontSize="large" />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
                                                        {notice.title}
                                                    </Typography>
                                                    <Chip 
                                                        icon={<EventIcon sx={{ fontSize: '1rem !important' }} />} 
                                                        label={dateString} 
                                                        size="small" 
                                                        sx={{ 
                                                            bgcolor: '#f1f5f9', 
                                                            color: '#64748b', 
                                                            fontWeight: 600,
                                                            borderRadius: '8px',
                                                            mb: 2
                                                        }} 
                                                    />
                                                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                                        {notice.details}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Edit Notice">
                                                    <IconButton 
                                                        onClick={() => navigate("/Admin/addnotice", { state: { notice } })}
                                                        sx={{ 
                                                            color: '#3b82f6', 
                                                            bgcolor: '#eff6ff',
                                                            borderRadius: '12px',
                                                            '&:hover': { bgcolor: '#bfdbfe', color: '#1d4ed8' }
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Notice">
                                                    <IconButton 
                                                        onClick={() => handleDeleteClick(notice._id)}
                                                        sx={{ 
                                                            color: '#ef4444', 
                                                            bgcolor: '#fee2e2',
                                                            borderRadius: '12px',
                                                            '&:hover': { bgcolor: '#fca5a5', color: '#b91c1c' }
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </PremiumCard>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </ContentContainer>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog}
                onClose={() => setDeleteDialog(false)}
                PaperProps={{
                    sx: { borderRadius: '24px', p: 2, minWidth: 400 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 900, color: '#1e293b' }}>
                    <ErrorOutlineIcon color="error" /> Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#64748b', fontWeight: 500, fontSize: '1.1rem' }}>
                        Are you sure you want to delete this notice? This action is permanent and cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                        onClick={() => setDeleteDialog(false)} 
                        variant="outlined"
                        sx={{ borderRadius: '12px', fontWeight: 700, color: '#64748b', borderColor: '#cbd5e1' }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmDelete} 
                        variant="contained" 
                        color="error"
                        sx={{ borderRadius: '12px', fontWeight: 700 }}
                        disableElevation
                    >
                        Delete Notice
                    </Button>
                </DialogActions>
            </Dialog>

        </PageWrapper>
    );
};

export default ShowNotices;